import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material'

import { RootState } from 'store'
import { useListDriversQuery } from 'store/drivers/drivers.api'
import { Driver } from 'store/drivers/drivers.type'
import {
  addDrivers,
  addFirstDriver,
} from 'store/trips/trips.slice'
import { DriverFixture } from 'store/trips/trips.fixtures'
import { getUserId } from 'utils/common'

type TripDriverSelectionPanelProps = {
  open: boolean
  onClose: () => void
}

const getDriverDisplayName = (driver: Driver) => {
  const name = `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim()
  return name || driver.phone || 'Driver'
}

const TripDriverSelectionPanel = ({
  open,
  onClose,
}: TripDriverSelectionPanelProps) => {
  const dispatch = useDispatch()
  const userId = getUserId()
  const firstDriver = useSelector((state: RootState) => state.trips.firstDriver)
  const secondDriver = useSelector(
    (state: RootState) => state.trips.secondDriver
  )
  const firstDriverIdFromPayload = useSelector(
    (state: RootState) => state.trips.tripsPayload.firstDriverId
  )

  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null)

  const { data: drivers } = useListDriversQuery(
    { userId, driverAppStatus: 'ACTIVATED' },
    { skip: !userId || !open }
  )

  const availableDrivers = useMemo(() => drivers ?? [], [drivers])
  const currentDriverId = firstDriver.id || firstDriverIdFromPayload || null

  useEffect(() => {
    if (!open) {
      setSelectedDriverId(null)
      return
    }
    setSelectedDriverId(currentDriverId)
  }, [open, currentDriverId])

  const handleSelect = (driverId: string) => {
    setSelectedDriverId((prev) => (prev === driverId ? null : driverId))
  }

  const handleConfirm = () => {
    if (!selectedDriverId) {
      dispatch(addFirstDriver(DriverFixture))
      dispatch(addDrivers([DriverFixture, secondDriver.id ? secondDriver : DriverFixture]))
      onClose()
      return
    }

    const selected =
      availableDrivers.find((driver) => driver.id === selectedDriverId) ??
      DriverFixture

    dispatch(addFirstDriver(selected))
    dispatch(
      addDrivers([
        selected,
        secondDriver.id ? secondDriver : DriverFixture,
      ])
    )
    onClose()
  }

  const canConfirm = selectedDriverId != null || !!currentDriverId

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography fontWeight={700} fontSize="1.05rem">
            Select Driver
          </Typography>
        </Stack>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <Stack
            spacing={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            {availableDrivers.map((driver, index) => {
              const isSelected = selectedDriverId === driver.id
              const displayName = getDriverDisplayName(driver)
              const showPhone = Boolean(
                `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim() &&
                  driver.phone
              )

              return (
                <Box key={driver.id}>
                  {index > 0 && <Divider />}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    onClick={() => handleSelect(driver.id)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'primary.light' : 'common.white',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => handleSelect(driver.id)}
                      sx={{ p: 0.5 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={700}>{displayName}</Typography>
                      {showPhone && (
                        <Typography variant="body2" color="text.secondary">
                          {driver.phone}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              )
            })}
            {availableDrivers.length === 0 && (
              <Typography sx={{ p: 2 }} color="text.secondary">
                No drivers available.
              </Typography>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={!canConfirm}
            onClick={handleConfirm}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 1.5,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Confirm Selection
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default TripDriverSelectionPanel
